
// Type guard utilities for safe property access
import { EnrichedData } from '@/components/clients/enrichment-dialog/enrichmentTypes';

export function hasRawProperty(value: unknown): value is { raw: unknown } {
  return typeof value === 'object' && value !== null && 'raw' in value;
}

export function isObjectWithProperty<T extends string>(
  value: unknown, 
  property: T
): value is Record<T, unknown> {
  return typeof value === 'object' && value !== null && property in value;
}

export function safelyGetRawProperty(value: unknown): unknown {
  if (hasRawProperty(value)) {
    return value.raw;
  }
  return null;
}

// Type guard for SystemError
export function isSystemError(value: unknown): value is { 
  id: string;
  user_id: string | null;
  error_type: string;
  error_message: string;
  url: string | null;
  severity: string;
  resolved: boolean;
  created_at: string;
} {
  return typeof value === 'object' && value !== null &&
    'id' in value && typeof (value as any).id === 'string' &&
    'error_type' in value && typeof (value as any).error_type === 'string' &&
    'error_message' in value && typeof (value as any).error_message === 'string' &&
    'severity' in value && typeof (value as any).severity === 'string' &&
    'resolved' in value && typeof (value as any).resolved === 'boolean' &&
    'created_at' in value && typeof (value as any).created_at === 'string';
}

// Type guard for UserActivityStats
export function isUserActivityStats(value: unknown): value is {
  user_id: string;
  email: string;
  last_login: string | null;
  total_logins: number;
} {
  return typeof value === 'object' && value !== null &&
    'user_id' in value && typeof (value as any).user_id === 'string' &&
    'email' in value && typeof (value as any).email === 'string' &&
    'total_logins' in value && typeof (value as any).total_logins === 'number';
}

// Generic function to filter valid data using a type guard
export function filterValidData<T>(data: unknown[], guard: (item: unknown) => item is T): T[] {
  return data.filter(guard);
}

// Type guard for safe updates in enrichment
export function isSafeForUpdates(value: unknown): value is unknown[] | Record<string, unknown> | string | number | boolean | null {
  return Array.isArray(value) || 
         (typeof value === 'object' && value !== null) ||
         typeof value === 'string' || 
         typeof value === 'number' || 
         typeof value === 'boolean' || 
         value === null;
}

// Enhanced type guard for enrichment dialog safe casting
export function isSafeEnrichmentValue(value: unknown): value is Record<string, unknown> | unknown[] {
  return Array.isArray(value) || 
         (typeof value === 'object' && value !== null && !Array.isArray(value));
}

// Safe casting function with fallback
export function safelyCastToEnrichmentValue(value: unknown): Record<string, unknown> | unknown[] {
  if (isSafeEnrichmentValue(value)) {
    return value;
  }
  // Fallback to empty object for safety
  return {};
}

// Expert-level type assertion with runtime validation for EnrichedData specifically
export function assertEnrichedData(value: unknown, context?: string): EnrichedData {
  // Runtime validation for EnrichedData interface
  if (value === null || value === undefined) {
    console.warn(`[EnrichedData Assertion] ${context || 'Unknown context'}: Received null/undefined, returning empty EnrichedData object`);
    return {};
  }
  
  // Arrays are not valid EnrichedData - convert to object
  if (Array.isArray(value)) {
    console.warn(`[EnrichedData Assertion] ${context || 'Unknown context'}: Received array, converting to empty EnrichedData object`);
    return {};
  }
  
  // Only objects can be EnrichedData
  if (typeof value === 'object') {
    return value as EnrichedData;
  }
  
  // Fallback for primitive types
  console.warn(`[EnrichedData Assertion] ${context || 'Unknown context'}: Received primitive type ${typeof value}, returning empty EnrichedData object`);
  return {};
}

// Keep the generic assertEnrichmentValue for other uses
export function assertEnrichmentValue(value: unknown, context?: string): Record<string, unknown> | unknown[] {
  // Runtime validation
  if (value === null || value === undefined) {
    console.warn(`[Type Assertion] ${context || 'Unknown context'}: Received null/undefined, returning empty object`);
    return {};
  }
  
  if (Array.isArray(value)) {
    return value;
  }
  
  if (typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  
  // Fallback for primitive types
  console.warn(`[Type Assertion] ${context || 'Unknown context'}: Received primitive type ${typeof value}, wrapping in object`);
  return { value };
}
